import React, { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';
import adaEmbed from '@ada-support/embed2';
import enLocale from 'date-fns/locale/en-GB';
import { useQuery } from 'react-query';
import localesMap from '../i18n/localesMap.json';
import ErrorLoadingData from '../components/ErrorLoadingData';
import { API_V1_HOSTNAME_KEY, ENVIRONMENT_KEY } from '../localStorageKeys';
import { ApiContext, ErrorObject } from './ApiContext';
import isCompatible, { ADA_MINIMUM_SPECS } from '../helpers/browserCompatibility';
import { isInFeature } from '../helpers/helpers';
import userLabsFeatures from '../helpers/userLabsFeatures';
import { Feature, User } from '../types';

const DEFAULT_LOCALE = 'en-GB';

export type UserRole = {
  RoleId: string;
  RoleName: string;
  Description: string;
};

export const userRoles = {
  moduleStandalone: '_EposStandalonePayments',
  modulePayments: '_EposNowPayments',
  moduleBalancePlatform: '_EposNowBalancePlatform',
  moduleNewOnboardingUrl: '_EposNowNewOnboardingURL',
  modulePayroll: 'ModulePayroll',
  moduleLabourReport: 'ModuleLabourReport',
  moduleMeasurement: 'ModuleMeasurement',
  moduleLoyalty: 'ModuleLoyalty',
  moduleChipAndPin: 'ModuleChipAndPin',
  moduleDeferReportExecution: 'ModuleDeferReportExecution',
  moduleTable: 'ModuleTable',
  moduleTableStatus: 'ModuleTableStatus',
  moduleReceiptDNI: 'ModuleReceiptDNI',
  moduleNewsAgent: 'ModuleNewsagent',
  moduleWaiterBanking: 'ModuleWaiterBanking',
  moduleLabs: 'ModuleLabs',
  moduleCorporate: 'ModuleCorporate',
  moduleLocations: 'ModuleLocations',
  moduleUserflow: 'ModuleUserflow',
};

export const isInRole = (userModules: UserRole[] | undefined, module: string) =>
  userModules?.filter((el) => el.RoleName === module)?.length > 0;

const UserContext = React.createContext({
  locale: DEFAULT_LOCALE,
  localeFns: enLocale,
  user: undefined as User,
  userModules: [] as UserRole[],
  userFeatures: [] as Feature[],
});

let didInit = false;
const UserContextProvider = ({ children }: { children: JSX.Element }) => {
  const { t, i18n } = useTranslation();
  const [locale, setLocale] = useState<string>(DEFAULT_LOCALE);
  const [localeFns, setLocaleFns] = useState<Locale>(enLocale);
  const { fetchIsReady, apiFetch } = useContext(ApiContext);
  const [userModules, setUserModules] = useState([] as UserRole[]);
  const labsFeatures = [] as Feature[];

  const importLocaleFile = async (lang: string) => {
    const localeToSet = await import(`date-fns/locale/${lang}/index.js`);
    setLocaleFns(localeToSet.default);
  };

  const changeLanguage = (language: string) => {
    let lang = language;
    let dateLang = language;
    if (localesMap.locales[lang]) {
      dateLang = localesMap.locales[lang];
    } else {
      lang = DEFAULT_LOCALE;
      dateLang = DEFAULT_LOCALE;
    }

    setLocale(lang);
    i18n.changeLanguage(lang).then(() => {
      importLocaleFile(dateLang).then();
    });
    document.documentElement.setAttribute('lang', lang);
  };

  const {
    refetch: fetchData,
    data: user,
    isLoading,
    isError,
  } = useQuery<User, ErrorObject>(
    'user',
    () => apiFetch(`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/user`),
    {
      enabled: fetchIsReady,
      onSuccess: (data: User) => {
        changeLanguage(data.locale);
      },
    }
  );

  const { isLoading: isLoadingModules } = useQuery(
    'userModules',
    () => apiFetch(`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/user-modules`),
    {
      enabled: fetchIsReady,
      onSuccess(data: UserRole[]) {
        setUserModules(data);
      },
    }
  );

  useEffect(() => {  
    if (!user) return;
    // Don’t run anything if LogRocket is disabled
    if (localStorage.getItem(ENVIRONMENT_KEY) !== 'production') return;

    try {
      LogRocket.identify(String(user.id), {
        name: user.fullName,
        userName: user.userName,
        email: user.email,
        location: user.location,
        locale: user.locale,
        isEmailVerified: String(user.isEmailVerified),
        isTwoFactorEnabled: String(user.isTwoFactorEnabled),
      });
    } catch (e) {
      Sentry.captureException(
        new Error('Error Identifying User In LogRocket'),
        { extra: { error: JSON.stringify(e) } }
      );
    }
  }, [user]);

  useEffect(() => {
    if (
      !didInit &&
      process.env.NODE_ENV !== 'test' &&
      user &&
      labsFeatures &&
      isInFeature(labsFeatures, userLabsFeatures.sideKick) &&
      isCompatible(ADA_MINIMUM_SPECS)
    ) {
      didInit = true;
      // https://developers.ada.cx/reference/embed2-reference#language
      // ADA only accepts two-letter codes (set 1) of ISO 639-1 language format.
      const language =
        localesMap?.locales && user.locale in localesMap.locales
          ? localesMap.locales[user.locale]
          : localesMap.locales[DEFAULT_LOCALE];
      adaEmbed.start({
        handle: 'eposnow-gen',
        language: language.substring(0, 2),
        metaFields: {
          guid: user.id,
          environment: 'Live',
        },
      });
    }
  }, [labsFeatures, user]);

  if (isError) {
    return <ErrorLoadingData action={fetchData} />;
  }

  if (!fetchIsReady) {
    return null;
  }

  if (isLoading || isLoadingModules || !user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '96px',
        }}
      >
        <CircularProgress
          aria-label={t('components.loading.loadingUserData')}
          aria-live="polite"
          aria-busy={isLoading || isLoadingModules || !user}
          data-testid="loading-icon"
          sx={{ color: 'text.secondary' }}
        />
      </Box>
    );
  }

  return (
    <UserContext.Provider
      value={{
        locale,
        localeFns,
        user,
        userModules,
        userFeatures: labsFeatures,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserContext, UserContextProvider };
